// Skills.tsx
import React from "react";
import {
  Box,
  Heading,
  SimpleGrid,
  Text,
  Flex,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Wrap,
  WrapItem,
  Tag,
} from "@chakra-ui/react";
import {
  skillsData,
  surface,
  surfaceAlt,
  border,
  accent,
} from "../constants/constants";
import Reveal from "./base/Reveal";

const Skills: React.FC = () => {
  return (
    <Box
      as="section"
      id="skills"
      aria-labelledby="skills-heading"
      py={{ base: 20, md: 28 }}
      bg="brand.bg"
      scrollMarginTop={{ base: "70px", md: "90px" }}
    >
      <Flex
        flexDirection={{ base: "column", md: "row" }}
        gap={{ base: 10, md: 16 }}
        maxWidth="1100px"
        mx="auto"
        px={{ base: 6, md: 10 }}
      >
        <Box flex={{ md: "0 0 220px" }}>
          <Reveal>
            <Heading
              id="skills-heading"
              fontSize={{ base: "4xl", md: "5xl" }}
              fontWeight={900}
              color="brand.text"
            >
              Skills
            </Heading>
          </Reveal>
        </Box>

        <Box flex="1">
          <Reveal delay={0.1}>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="brand.muted"
              lineHeight="1.7"
              mb={12}
              maxWidth="600px"
            >
              I started out as a backend engineer and still love all things
              backend, though I&apos;ve since built up solid frontend chops too.
              Working in the disability sector taught me how much accessible
              design matters, and I&apos;ve picked up experience with security,
              load testing, and AI-assisted development along the way.
            </Text>
          </Reveal>

          {/* Desktop / tablet: unchanged multi-column layout */}
          <SimpleGrid
            display={{ base: "none", md: "grid" }}
            columns={3}
            spacingX={10}
            spacingY={8}
          >
            {Object.keys(skillsData).map((key, index) => {
              const category = skillsData[key];
              return (
                <Reveal key={key} delay={0.15 + index * 0.05}>
                  <VStack align="flex-start" spacing={2}>
                    <Text fontWeight={700} color="brand.text" mb={1}>
                      {category.category}
                    </Text>
                    {category.skills.map((skill) => (
                      <Text key={skill.name} fontSize="md" color="brand.muted">
                        {skill.name}
                      </Text>
                    ))}
                  </VStack>
                </Reveal>
              );
            })}
          </SimpleGrid>

          {/* Mobile: collapsible accordion so the page isn't one long stacked list */}
          <Box display={{ base: "block", md: "none" }}>
            <Reveal delay={0.15}>
              <Accordion allowToggle>
                {Object.keys(skillsData).map((key) => {
                  const category = skillsData[key];
                  return (
                    <AccordionItem
                      key={key}
                      bg={surface}
                      border="1px solid"
                      borderColor={border}
                      borderRadius="xl"
                      mb={3}
                      overflow="hidden"
                    >
                      <AccordionButton
                        py={4}
                        px={4}
                        _hover={{ bg: surfaceAlt }}
                        _expanded={{ color: accent }}
                      >
                        <Text
                          flex="1"
                          textAlign="left"
                          fontWeight={700}
                          color="inherit"
                        >
                          {category.category}
                        </Text>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel px={4} pb={4}>
                        <Wrap spacing={2}>
                          {category.skills.map((skill) => (
                            <WrapItem key={skill.name}>
                              <Tag
                                size="md"
                                variant="subtle"
                                bg="brand.accentMuted"
                                color={accent}
                              >
                                {skill.name}
                              </Tag>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </AccordionPanel>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </Reveal>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default Skills;
